import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

import 'hammerjs';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr'; // For auth after login toast

import { CoreModule } from '@core/core.module';
import { CoreCommonModule } from '@core/common.module';
import { CoreSidebarModule, CoreThemeCustomizerModule } from '@core/components';

import { coreConfig } from 'app/app-config';

import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import { TokenInterceptor } from './auth/token.interceptor';
import { AuthGuard } from './auth/helpers';
import { CompanyService } from './main/company/company.service';
import { EndpointService } from './main/endpoint/endpoint.service';
import { ServiceService } from './main/service/service.service';
import { PartService } from './main/part/part.service';
import { CounterpartyService } from './main/counterparty/counterparty.service';
import { OrderService } from './main/order/order.service';
import { DeliveryService } from './main/delivery/delivery.service';

const appRoutes: Routes = [
  {
    path: '',
    redirectTo: '/orders/list/all',
    pathMatch: 'full'
  },
  {
    path: '',
    loadChildren: () => import('./main/dashboard/dashboard.module').then(m => m.DashboardModule)
  },
  {
    path: 'invoice',
    loadChildren: () => import('./main/invoice/invoice.module').then(m => m.InvoiceModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'orders',
    loadChildren: () => import('./main/order/order.module').then(m => m.OrderModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'deliveries',
    loadChildren: () => import('./main/delivery/delivery.module').then(m => m.DeliveryModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'sales',
    loadChildren: () => import('./main/sale/sale.module').then(m => m.SaleModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'parts',
    loadChildren: () => import('./main/part/part.module').then(m => m.PartModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'counterparties',
    loadChildren: () => import('./main/counterparty/counterparty.module').then(m => m.CounterpartyModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'endpoints',
    loadChildren: () => import('./main/endpoint/endpoint.module').then(m => m.EndpointModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'services',
    loadChildren: () => import('./main/service/service.module').then(m => m.ServiceModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner', 'worker']
    }
  },
  {
    path: 'settings',
    loadChildren: () => import('./main/settings/settings.module').then(m => m.SettingsModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner']
    }
  },
  {
    path: 'stats',
    loadChildren: () => import('./main/stat/stat.module').then(m => m.StatModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner']
    }
  },
  {
    path: 'stats-orders',
    loadChildren: () => import('./main/stat-orders/stat-orders.module').then(m => m.StatOrdersModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin', 'owner']
    }
  },
  {
    path: 'companies',
    loadChildren: () => import('./main/company/company.module').then(m => m.CompanyModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin']
    }
  },
  {
    path: 'users',
    loadChildren: () => import('./main/user/user.module').then(m => m.UserModule),
    canActivate: [AuthGuard],
    data: {
      roles: ['admin']
    }
  },
  {
    path: '**',
    redirectTo: '/error' //Error 404 - Page not found
  }
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: 'enabled', // Add options right here
      relativeLinkResolution: 'legacy'
    }),
    TranslateModule.forRoot(),

    //NgBootstrap
    NgbModule,
    ToastrModule.forRoot(),

    // Core modules
    CoreModule.forRoot(coreConfig),
    CoreCommonModule,
    CoreSidebarModule,
    CoreThemeCustomizerModule,

    // App modules
    LayoutModule
  ],

  bootstrap: [AppComponent],

  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    // Service Providers
    OrderService,
    PartService,
    CounterpartyService,
    DeliveryService,
    CompanyService,
    EndpointService,
    ServiceService
  ]
})
export class AppModule { }
